import { z } from "zod";
// Profiles, the looking-for-players board and challenges. A separate file
// because this one is long enough, and because that feature set has one
// governing rule -- nothing is visible until its owner publishes it -- which
// is easier to keep true when it lives together.
export * from "./social.js";
/**
 * The contract between every client and the API.
 *
 * This file is imported by the API, the worker, the desktop Studio and the web
 * dashboard. When an event shape changes here, the other four break at compile
 * time — which is the only thing standing between you and a silent mismatch
 * that you discover three weeks later in a stream recording you can't clip.
 */
export const EVENT_SCHEMA_VERSION = 1;
// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
export const EventKind = z.enum([
    "session_start",
    "sync_marker",
    "video_start",
    "video_pause",
    "video_resume",
    "video_end",
    "video_skip",
    "reaction_start",
    "reaction_end",
    "mark_start",
    "mark_end",
    // A soundboard pad fired. Recorded because reaching for an airhorn is a
    // strong signal about where the moment was -- deriveReactions does not switch
    // on it, so it cannot move a clip boundary.
    "sound",
    // A burst of audience reactions. Recorded as a note on the timeline;
    // deriveReactions does not switch on it, so it cannot move a clip boundary.
    "audience_peak",
    // The streamer's own mic went well above their own normal -- a shout or a
    // laugh. Detected on their machine from levels already in memory; only the
    // timestamp and the ratio are sent, never audio. Same standing as the kinds
    // around it: deriveReactions does not switch on it, so it cannot move a clip
    // boundary. Its one effect is to suggest a clip for approval.
    "voice_peak",
    // A game was launched from Studio, and later left the process list. Which
    // game is in the event's metadata. Notes on the timeline, so that clips from
    // a gameplay session can be named and grouped by what was being played --
    // deriveReactions does not switch on either, so neither can move a clip
    // boundary. Clips in a gameplay session still come from the streamer's
    // hotkeys and the crowd's reactions, as they always have.
    "game_start",
    "game_end",
    "session_end",
]);
export const PlaybackEvent = z.object({
    /**
     * Generated by the client, not the server. This is what makes retries safe:
     * the Studio buffers events to disk and re-POSTs batches after a network
     * drop, and the server can discard duplicates instead of writing a second
     * reaction_start that would corrupt the clip boundaries.
     */
    id: z.string().min(8).max(64),
    kind: EventKind,
    video_id: z.string().nullable().optional(),
    /**
     * Milliseconds since the session's start sync marker, measured with a
     * monotonic clock. Never derive this from Date.now() — an NTP correction
     * mid-session would shift every subsequent boundary.
     */
    offset_ms: z.number().int().min(0),
    /** Wall clock, for debugging and fallback alignment only. Never for math. */
    wall_clock: z.string().datetime(),
    /** Position within the source video when the event fired. */
    media_time_ms: z.number().int().min(0).nullable().optional(),
    metadata: z.record(z.unknown()).default({}),
});
export const EventBatch = z.object({
    events: z.array(PlaybackEvent).min(1).max(200),
});
// ---------------------------------------------------------------------------
// Reaction derivation
// ---------------------------------------------------------------------------
/**
 * Padding applied to every generated clip. A reaction cut exactly on the
 * boundary looks clipped; a couple of seconds of air looks deliberate.
 */
/**
 * Timed marks.
 *
 * A streamer reacting full-screen, or working through one long video with no
 * per-clip boundaries, needs to say "the interesting part is around now" — but
 * by the time they know it was interesting, it has already happened.
 *
 * Because an event carries an offset into the recording and derivation runs
 * server-side after upload, a mark can be back-dated freely: the recording
 * already contains the past, and the forward half will exist by the time the
 * file is uploaded. Nothing has to be buffered in Studio.
 *
 * Two sizes rather than one adjustable value: mid-stream a streamer picks a
 * button, not a number.
 */
/**
 * Plans whose rendered clips carry a watermark.
 *
 * Shared so the worker (which burns it in) and the API (which decides a clip is
 * stale because it still carries one) cannot drift. Only the trial: a lapsed
 * account renders nothing new, and marking a paying customer's work would be a
 * strange thing to do to someone funding the service.
 */
export const WATERMARKED_PLANS = new Set(["trial"]);
export const MARK_WINDOWS = {
    short: { backMs: 150_000, forwardMs: 150_000 }, // 5 minutes total
    long: { backMs: 300_000, forwardMs: 300_000 }, // 10 minutes total
};
/**
 * Padding either side of a clip-mode span. The boundary is the video's own
 * start and end; this is only so a badly-placed keyframe cannot shave the first
 * or last frame. Well under the post-clip countdown, which clip mode exists to
 * keep out of the file.
 */
export const CLIP_MODE_PAD_MS = 2000;
export const CLIP_PAD_IN_MS = 1500;
export const CLIP_PAD_OUT_MS = 2000;
/**
 * A replay is a reaction that reopens on the same video with nothing else
 * played in between. Anything under this gap folds into the previous span
 * rather than becoming its own clip.
 *
 * Two minutes covers rewatching a short-form clip a few times while still
 * talking over it. Coming back to the same video later in the stream, after
 * other videos, is a genuinely separate reaction and is never merged — that
 * case is caught by the different-video check, not by this timer.
 */
export const REPLAY_MERGE_GAP_MS = 120_000;
/**
 * Walks an event stream and produces clip boundaries.
 *
 * Lives in shared rather than in the worker because the Studio runs the exact
 * same function to preview boundaries locally. Two implementations of this
 * would drift apart and you would not notice until a customer's clips came out
 * wrong.
 *
 * In `reaction` mode the spans are driven by reaction_start/reaction_end; in
 * `clip` mode by video_start/video_end, with no padding, because padding there
 * would reintroduce exactly the countdown tail the mode exists to exclude.
 *
 * Manual marks win over automatic ones in both modes: if the streamer pressed
 * the button, they were correcting something, so an open automatic span is
 * replaced rather than nested.
 *
 * Replays are merged. A streamer who watches a clip, reacts, replays it, and
 * reacts again has performed one bit and wants one clip — emitting a fragment
 * per playback would hand them three unusable pieces. The merge only applies
 * while the same video is on screen: once a different video starts, the next
 * reaction on the original is a separate span no matter how soon it happens.
 */
export function deriveReactions(events, mode = "reaction") {
    const clipMode = mode === "clip";
    // The boundary in clip mode is still the video's own start and end — the pad
    // is only breathing room so the first and last frame are not clipped by a
    // keyframe landing badly. Kept to two seconds, comfortably inside the
    // countdown the mode exists to exclude.
    const padIn = clipMode ? CLIP_MODE_PAD_MS : CLIP_PAD_IN_MS;
    const padOut = clipMode ? CLIP_MODE_PAD_MS : CLIP_PAD_OUT_MS;
    const sorted = [...events].sort((a, b) => a.offset_ms - b.offset_ms);
    const out = [];
    let open = null;
    // Set when a video other than the last reacted-to one starts playing. Once
    // that happens the previous span is closed for good.
    let switchedVideo = false;
    const close = (endMs, endEvent) => {
        if (!open || endMs <= open.offset_ms) {
            open = null;
            return;
        }
        const videoId = open.video_id ?? null;
        const previous = out[out.length - 1];
        const isReplay = previous !== undefined &&
            !switchedVideo &&
            previous.video_id === videoId &&
            videoId !== null &&
            open.offset_ms - previous.raw_end_ms <= REPLAY_MERGE_GAP_MS &&
            // A manual mark is an explicit instruction to start a new span; never
            // swallow it into the previous one.
            open.kind !== "mark_start" &&
            endEvent?.kind !== "mark_end";
        if (isReplay) {
            previous.raw_end_ms = endMs;
            previous.end_ms = endMs + padOut;
            previous.segments += 1;
        }
        else {
            out.push({
                ordinal: out.length + 1,
                video_id: videoId,
                start_ms: Math.max(0, open.offset_ms - padIn),
                end_ms: endMs + padOut,
                raw_start_ms: open.offset_ms,
                raw_end_ms: endMs,
                segments: 1,
            });
        }
        open = null;
    };
    for (const ev of sorted) {
        switch (ev.kind) {
            case "video_start": {
                // Recomputed on every start, not latched. An earlier version only ever
                // set this true, which meant that once any video change occurred, no
                // later video could merge its own replays — replay handling silently
                // worked for the first clip of a stream and nothing after it.
                const last = out[out.length - 1];
                switchedVideo = last !== undefined && ev.video_id !== last.video_id;
                // The video starting opens the span in both modes. A reaction clip is
                // the video *and* the reaction to it — opening at video_end instead
                // produced clips containing only the camera, with the thing being
                // reacted to missing. A previous span left open (a video that never
                // reported its end) closes here rather than swallowing the next one.
                // A manual mark is an explicit span the streamer asked for, so a
                // video starting must neither cut it short nor replace it —
                // otherwise pressing Mark and letting the next clip roll emits a
                // stub of whatever happened to be on screen.
                if (open && open.kind === "mark_start")
                    break;
                if (open)
                    close(ev.offset_ms, null);
                open = ev;
                break;
            }
            case "video_end":
                // Clip mode stops with the video; reaction mode runs on to Next.
                if (clipMode)
                    close(ev.offset_ms, ev);
                break;
            case "video_skip":
                // Emitted only when Next is pressed while the video is still playing —
                // the streamer abandoned it without reacting. A real reaction always
                // ends with reaction_end instead, so discarding here cannot lose one.
                // Without this, skipping past a clip left its span open until the next
                // video started, emitting a clip of something nobody reacted to.
                open = null;
                break;
            case "reaction_start":
                // Emitted when a video ends, by which point reaction mode already has a
                // span open from video_start. It only opens one itself when nothing is
                // playing — a webcam-only session, where there is no video to open it.
                if (!clipMode && !open)
                    open = ev;
                break;
            case "mark_start":
                open = ev; // manual correction supersedes an open automatic span
                break;
            case "reaction_end":
                if (!clipMode)
                    close(ev.offset_ms, ev);
                break;
            case "mark_end":
                close(ev.offset_ms, ev);
                break;
            case "session_end":
                // Close a dangling reaction rather than dropping it. A streamer who
                // ends the session mid-reaction still wants that clip. No trailing pad:
                // there is no recording left to pad into.
                if (open && ev.offset_ms > open.offset_ms) {
                    close(ev.offset_ms, null);
                    const last = out[out.length - 1];
                    if (last)
                        last.end_ms = ev.offset_ms;
                }
                open = null;
                break;
        }
    }
    return out;
}
export function toRecordingTime(offsetMs, syncOffsetMs, driftRatio = 1) {
    return Math.round(syncOffsetMs + offsetMs * driftRatio);
}
// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const RegisterInput = z.object({
    email: z.string().email(),
    password: z.string().min(10).max(200),
    display_name: z.string().min(1).max(80).optional(),
    /**
     * Acceptance of the Terms and Privacy Policy. Required, and recorded against
     * the account with a timestamp: for a service hosting other people's video,
     * the rights warranty in those terms is the clause that matters most, and an
     * acceptance nobody stored is one you cannot rely on later.
     */
    accepted_terms: z.literal(true),
    /**
     * Which product the person signed up from, so the confirmation email and
     * the page its link opens speak about that product. One account either way.
     */
    app: z.enum(["studio", "streamnexa"]).optional(),
});
export const LoginInput = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(200),
});
export const RefreshInput = z.object({ refresh_token: z.string().min(10) });
export const DeviceTokenInput = z.object({
    label: z.string().min(1).max(80).default("Reaction Studio"),
});
// ---------------------------------------------------------------------------
// Videos and queue
// ---------------------------------------------------------------------------
export const VideoSource = z.enum(["local", "uploaded"]);
/**
 * Registers a file the streamer already has on disk. The bytes never leave
 * their machine in v1 — the server stores the record so the queue, the event
 * log and the eventual clips can all reference a stable video_id.
 */
export const RegisterLocalVideoInput = z.object({
    title: z.string().max(300).optional(),
    local_filename: z.string().min(1).max(500),
    /** size + head/tail hash. See the migration for why it isn't a full hash. */
    content_hash: z.string().min(8).max(128),
    size_bytes: z.number().int().positive(),
    duration_ms: z.number().int().positive().optional(),
    /**
     * Pixel dimensions. Portrait sources are letterboxed into a narrow column on a
     * 16:9 stage, and both the camera's position and any export crop depend on
     * knowing where that column is.
     */
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    /** Optional attribution, typically from the tokenless Meta oEmbed endpoint. */
    origin_url: z.string().url().optional(),
    origin_platform: z.string().max(40).optional(),
    origin_author: z.string().max(120).optional(),
    thumbnail_url: z.string().url().optional(),
    /** Append to the queue on creation. Almost always what you want. */
    enqueue: z.boolean().default(true),
});
export const ReorderQueueInput = z.object({
    /** Neighbours to drop between. Omit before for the top, after for the end. */
    before_id: z.string().nullable().optional(),
    after_id: z.string().nullable().optional(),
});
export const QueueStateInput = z.object({
    state: z.enum(["waiting", "done", "skipped"]),
});
export const PlaybackStateInput = z.object({
    current_item_id: z.string().nullable(),
    position_ms: z.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------
export const CreateSessionInput = z.object({
    started_at: z.string().datetime(),
    client_version: z.string().max(40).optional(),
});
/**
 * Validates a camera rect reported by Studio, for storage.
 *
 * Deliberately *not* part of `CreateSessionInput`. A malformed layout detail
 * must never fail the call that starts a session — that call is the one thing
 * standing between a streamer and going live, and losing it over a crop hint
 * would be absurd. So this returns null and the session starts without it, the
 * same way `mark_mode` falls back rather than rejecting.
 *
 * The worker checks the rect again before using it, against the actual frame
 * size. That is not redundant: this answers "is this a plausible rect at all",
 * which is a storage question, and the worker answers "can I build a legal crop
 * from it for this particular recording", which is not.
 */
export function parseCamRect(value) {
    if (!value || typeof value !== "object")
        return null;
    const v = value;
    const nums = {};
    for (const k of ["x", "y", "w", "h"]) {
        const n = v[k];
        if (typeof n !== "number" || !Number.isFinite(n))
            return null;
        nums[k] = n;
    }
    const { x, y, w, h } = nums;
    // A camera smaller than 1% of the frame is not a camera; something upstream
    // measured a collapsed element.
    if (w <= 0.01 || h <= 0.01)
        return null;
    // The tolerance absorbs rounding: the rect is derived from a pixel rect
    // divided by a pixel stage size, so a box flush against an edge can land a
    // hair past 1.
    const TOL = 0.02;
    if (x < -TOL || y < -TOL)
        return null;
    if (x + w > 1 + TOL || y + h > 1 + TOL)
        return null;
    return { x, y, w, h };
}
export const SyncMarkerInput = z.object({
    marker: z.enum(["start", "end"]),
    wall_clock: z.string().datetime(),
    /** For the end marker: ms the Studio measured since the start marker. */
    offset_ms: z.number().int().min(0).optional(),
    method: z.string().max(40).default("flash_beep"),
    /**
     * Position inside the recording at this marker, when the streaming software
     * could report it (OBS can; Streamlabs cannot). Present on the start marker,
     * this IS sync_offset_ms and the worker skips audio detection.
     */
    recorder_offset_ms: z.number().int().min(0).optional(),
    recorder_kind: z.string().max(40).optional(),
});
export const EndSessionInput = z.object({ ended_at: z.string().datetime() });
// ---------------------------------------------------------------------------
// Recordings
// ---------------------------------------------------------------------------
/** 100 MiB parts. A 3-hour stream is typically 40-150 parts. */
export const UPLOAD_PART_SIZE = 100 * 1024 * 1024;
export const CreateRecordingInput = z.object({
    session_id: z.string().nullable().optional(),
    filename: z.string().min(1).max(500),
    size_bytes: z.number().int().positive(),
    duration_ms: z.number().int().positive().optional(),
    recorded_at: z.string().datetime().optional(),
    /**
     * Byte size of each part, when the client wants one other than
     * UPLOAD_PART_SIZE. Studio asks for small parts because every in-flight
     * part is a real buffer in its memory, where a browser only holds a
     * slice of a File. Clamped server-side; S3 will not accept a part under
     * 5 MiB except the last.
     */
    part_size: z.number().int().min(5 * 1024 * 1024).max(UPLOAD_PART_SIZE).optional(),
});
export const CompleteRecordingInput = z.object({
    parts: z
        .array(z.object({ number: z.number().int().positive(), etag: z.string().min(1) }))
        .min(1),
});
export const AlignRecordingInput = z.object({
    sync_offset_ms: z.number().int(),
});
export const EditReactionInput = z.object({
    start_ms: z.number().int().min(0).optional(),
    end_ms: z.number().int().min(0).optional(),
    title: z.string().max(300).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Background jobs
// ---------------------------------------------------------------------------
// Queue names and payloads live here so the API (which enqueues) and the worker
// (which consumes) cannot disagree about the shape of a job.
export const JOB_QUEUE = {
    media: "media",
};
/**
 * Queue priority. Lower runs sooner.
 *
 * Pro advertises "priority processing", which was sold for months without
 * existing — every job was enqueued unprioritised and the queue was plain FIFO.
 *
 * **BullMQ's zero is not neutral.** Its own types say it: "`0` means no explicit
 * priority, and jobs with no explicit priority are processed before prioritized
 * jobs." So prioritising only the paying tiers would have put everyone else
 * *ahead* of them — the exact inverse of the promise, silently. Every job this
 * system enqueues must therefore carry an explicit non-zero priority, including
 * the repeatable maintenance jobs.
 *
 * Derived from the plan's price rather than a map of plan ids, so a tier added
 * later sorts correctly on the day it is created instead of falling through a
 * `default` nobody remembered to update. Monotonic: more expensive is never
 * slower.
 */
const PRIORITY_BASE = 1000;
/** Housekeeping yields to every customer, always. */
export const JOB_PRIORITY_MAINTENANCE = 2000;
/**
 * The BullMQ priority for a plan costing `priceCents` per month.
 *
 * A missing or unreadable price is treated as free rather than as premium: the
 * dangerous direction is accidentally fast, because that is the case nobody
 * notices until a Pro customer complains.
 *
 * Known limit, deliberately not solved: priority here is strict, so a large
 * enough backlog from an expensive plan can hold up a cheaper one indefinitely.
 * With one worker and a queue that is empty most of the time this cannot bite,
 * and the remedy if it ever does is to band by job kind — every recording gets
 * analysed before any clip renders — rather than to weight or age the queue.
 */
export function jobPriority(priceCents) {
    const cents = typeof priceCents === "number" && Number.isFinite(priceCents) ? priceCents : 0;
    const dollars = Math.floor(Math.max(0, cents) / 100);
    // Clamped at 1 because 0 would mean "unprioritised", which BullMQ runs first.
    return Math.min(PRIORITY_BASE, Math.max(1, PRIORITY_BASE - dollars));
}
export const JobName = z.enum([
    "detect_sync", "render_clip", "sweep_uploads", "validate_submission",
]);
export const DetectSyncPayload = z.object({
    recording_id: z.string(),
    user_id: z.string(),
});
export const RenderClipPayload = z.object({
    clip_id: z.string(),
    user_id: z.string(),
});
export const ValidateSubmissionPayload = z.object({
    submission_id: z.string(),
    user_id: z.string(),
});
/** An On-Stream Ads video to judge from the file itself. See ext-ads.ts. */
export const ProbeExtAdPayload = z.object({
    ad_id: z.string(),
    user_id: z.string(),
});
export * from "./sync.js";
export * from "./post-plan.js";
// Clip rendering, moved out of the worker so Studio can render locally with
// the same code rather than a second implementation of it. All four are pure:
// they build ffmpeg arguments, caption cues and a tone detector, and touch no
// filesystem, network or process.
export * from "./clip-filters.js";
export * from "./reframe.js";
export * from "./captions.js";
export * from "./tone.js";
// The one place that decides what a recording’s clips are. Both the worker and
// Studio read it, which is what keeps a boundary from moving depending on which
// of them cut the clip.
export * from "./clip-plan.js";
export * from "./remote.js";
export * from "./loudness.js";
export * from "./reel.js";
export * from "./play-queue.js";
export * from "./free-games.js";
export * from "./couch-games.js";
export * from "./ext-ads.js";
// ---------------------------------------------------------------------------
// Paid submission limits
// ---------------------------------------------------------------------------
// Shared because the API states these to a submitter before they upload and the
// worker enforces them after. Two copies would drift, and the drift would show
// up as a file accepted by the form and rejected by the probe.
/**
 * A floor, not a match.
 *
 * Requiring a submission to match the streamer's output resolution would reject
 * the best files: the stage draws with `object-fit: contain`, so a 4K source
 * downscales to something sharper than a native 1080p one. A streamer's own
 * output also varies by scene and platform, so submitters could not target it.
 */
export const SUBMISSION_LIMITS = {
    minWidth: 1280,
    minHeight: 720,
    maxWidth: 3840,
    maxHeight: 2160,
    minMs: 15_000,
    maxMs: 300_000,
    maxBytes: 500 * 1024 * 1024,
    /** The one combination guaranteed to play in the Studio's WebView. */
    videoCodec: "h264",
    audioCodec: "aac",
};
/**
 * Whether an uploaded file can be accepted.
 *
 * `maxSeconds` comes from the tier they paid for, so the duration limit is
 * theirs rather than the global one — someone who bought a minute is told they
 * bought a minute, not that the maximum is five.
 *
 * Every rejection names the actual value and the required one. "Invalid file"
 * costs a support message; "your video is 640x360, it needs to be at least
 * 1280x720" costs a re-export.
 */
export function checkSubmission(p, maxSeconds) {
    const L = SUBMISSION_LIMITS;
    const ok = { ok: true, reason: "" };
    if (!Number.isFinite(p.durationMs) || p.durationMs <= 0) {
        return { ok: false, reason: "That file has no video we could read." };
    }
    if (!Number.isFinite(p.width) || !Number.isFinite(p.height) || p.width <= 0 || p.height <= 0) {
        return { ok: false, reason: "That file has no picture we could read." };
    }
    if (p.sizeBytes > L.maxBytes) {
        const mb = Math.round(p.sizeBytes / 1024 / 1024);
        return { ok: false, reason: `That file is ${mb} MB. The limit is 500 MB.` };
    }
    // Checked against the tier, not the global maximum. A second of slack,
    // because encoders round and a 60.4s export against a 60s tier is not a
    // problem worth a support message.
    const allowedMs = Math.min(maxSeconds * 1000, L.maxMs);
    if (p.durationMs > allowedMs + 1000) {
        const got = Math.round(p.durationMs / 1000);
        return {
            ok: false,
            reason: `Your video is ${got} seconds. What you paid for allows up to ${Math.round(allowedMs / 1000)}.`,
        };
    }
    if (p.durationMs < L.minMs) {
        const got = Math.round(p.durationMs / 1000);
        return { ok: false, reason: `Your video is ${got} seconds. The minimum is 15.` };
    }
    // Judged on the short and long edges, not width against width. A 1080x1920
    // phone video is 1080p held sideways, and measuring its 1080 width against a
    // 1280 minimum would reject exactly the portrait material this product was
    // built to letterbox.
    const shortEdge = Math.min(p.width, p.height);
    const longEdge = Math.max(p.width, p.height);
    if (shortEdge < L.minHeight || longEdge < L.minWidth) {
        return {
            ok: false,
            reason: `Your video is ${p.width}x${p.height}. It needs to be at least ${L.minWidth}x${L.minHeight}.`,
        };
    }
    if (shortEdge > L.maxHeight || longEdge > L.maxWidth) {
        return {
            ok: false,
            reason: `Your video is ${p.width}x${p.height}. The maximum is ${L.maxWidth}x${L.maxHeight}.`,
        };
    }
    if (p.videoCodec.toLowerCase() !== L.videoCodec) {
        return {
            ok: false,
            reason: `That video uses ${p.videoCodec}. It needs to be H.264 in an MP4.`,
        };
    }
    // A silent video is allowed; a video with audio the player cannot decode is
    // not, because it would play as silence on the broadcast with no warning.
    if (p.audioCodec && p.audioCodec.toLowerCase() !== L.audioCodec) {
        return {
            ok: false,
            reason: `That audio uses ${p.audioCodec}. It needs to be AAC.`,
        };
    }
    return ok;
}
export const REACTIONS = [
    { kind: "lol", emoji: "\u{1F602}", label: "LOL" },
    { kind: "wtf", emoji: "\u{1F631}", label: "WTF" },
    { kind: "fire", emoji: "\u{1F525}", label: "Fire" },
    { kind: "dead", emoji: "\u{1F480}", label: "Dead" },
    { kind: "love", emoji: "❤️", label: "Love" },
    { kind: "mind", emoji: "\u{1F92F}", label: "Mind blown" },
    { kind: "cold", emoji: "\u{1F976}", label: "Ice cold" },
    { kind: "yeah", emoji: "\u{1F44D}", label: "Yeah" },
    { kind: "nah", emoji: "\u{1F44E}", label: "Nah" },
];
export const REACTION_KINDS = REACTIONS.map((r) => r.kind);
export function reactionDef(kind) {
    return REACTIONS.find((r) => r.kind === kind);
}
/** The burst a peak summarises reaches back about this far before detection. */
export const PEAK_BURST_MS = 5000;
/** A little room each side when turning a peak into a suggested clip window. */
export const PEAK_PAD_MS = 1500;
/**
 * Pulls audience-peak points out of a raw event stream.
 *
 * Tolerant of missing metadata: a peak with no hit count still counts as one
 * reaction's worth rather than being dropped, so a malformed row never silently
 * erases a moment the audience actually reacted to.
 */
export function peakPoints(events) {
    const points = [];
    for (const e of events) {
        if (e.kind !== "audience_peak")
            continue;
        const meta = (e.metadata ?? {});
        const hits = typeof meta.hits === "number" && meta.hits > 0 ? meta.hits : 1;
        const kind = typeof meta.kind === "string" ? meta.kind : "fire";
        points.push({ offsetMs: e.offset_ms, hits, kind });
    }
    return points;
}
/**
 * The audience score for one reaction: the hits of every peak inside its window.
 *
 * A peak counts for a reaction if it was detected between the reaction's start
 * and end. Zero when the audience was quiet through it — which is a real signal
 * too, not a gap.
 */
export function audienceScore(reaction, peaks) {
    let score = 0;
    for (const p of peaks) {
        if (p.offsetMs >= reaction.start_ms && p.offsetMs <= reaction.end_ms)
            score += p.hits;
    }
    return score;
}
/**
 * Peaks that fell outside every reaction — moments the audience loved but the
 * streamer never marked. These become *suggested* clips, never silent ones: the
 * audience can be gamed, so a human still confirms. Returned as windows ready to
 * store, widest-hit first so the strongest suggestion leads.
 */
export function autoMarkWindows(reactions, peaks) {
    const covered = (p) => reactions.some((r) => p.offsetMs >= r.start_ms && p.offsetMs <= r.end_ms);
    return peaks
        .filter((p) => !covered(p))
        .map((p) => ({
        // The burst reaches back before detection; pad both sides a little.
        start_ms: Math.max(0, p.offsetMs - PEAK_BURST_MS - PEAK_PAD_MS),
        end_ms: p.offsetMs + PEAK_PAD_MS,
        hits: p.hits,
        kind: p.kind,
    }))
        .sort((a, b) => b.hits - a.hits);
}
/**
 * How far back a voice suggestion reaches before the shout.
 *
 * Generous, and deliberately asymmetric: you shout *after* the thing that was
 * funny. A window centred on the shout would reliably cut off its own cause,
 * which is the one part of the clip that has to be in it.
 */
export const VOICE_LEAD_MS = 6000;
/** And a little after, for the reaction itself to land. */
export const VOICE_TAIL_MS = 3000;
/**
 * Most voice suggestions one recording may produce.
 *
 * The cooldown is 30 seconds, so a long stream could in principle emit
 * hundreds, and a list of hundreds is not a list anyone reads -- it is the
 * "scan and guess" experience this product exists to replace. The strongest
 * survive the cap, measured by how far above their own normal they were.
 *
 * Audience suggestions are deliberately uncapped: a crowd peak needs a minimum
 * number of distinct people and is the better-evidenced signal of the two.
 */
export const MAX_VOICE_SUGGESTIONS = 12;
/**
 * Pulls voice-peak points out of a raw event stream.
 *
 * Tolerant of missing metadata in the same way as `peakPoints`: a peak whose
 * ratio did not survive the round trip still counts, at the detector's own
 * threshold, rather than being dropped. The alternative is silently losing a
 * moment that really happened because one number was malformed.
 */
export function voicePoints(events) {
    const points = [];
    for (const e of events) {
        if (e.kind !== "voice_peak")
            continue;
        const meta = (e.metadata ?? {});
        const multiple = typeof meta.multiple === "number" && meta.multiple > 0 ? meta.multiple : 1;
        const level = typeof meta.level === "number" && meta.level > 0 ? meta.level : 0;
        points.push({ offsetMs: e.offset_ms, multiple, level });
    }
    return points;
}
/**
 * Every moment worth offering for approval, from both signals, deduplicated.
 *
 * Three rules, each of which exists because the naive version is wrong:
 *
 * 1. **One moment, one row.** A crowd burst and a shout at the same instant is
 *    one moment with two independent witnesses, not two clips of the same
 *    footage. It is reported as `both`, which is the strongest suggestion this
 *    product can make, and it leads the list.
 *
 * 2. **The crowd outranks the voice.** A peak that needed five distinct people
 *    is better evidence than one person getting loud, so audience suggestions
 *    come first and are never dropped to make room for a shout.
 *
 * 3. **Voice suggestions are capped.** See MAX_VOICE_SUGGESTIONS.
 *
 * Nothing here is ever clipped on its own: these are rows awaiting a human.
 */
export function suggestionWindows(reactions, peaks, voice) {
    const out = autoMarkWindows(reactions, peaks).map((w) => ({
        ...w,
        source: "audience",
    }));
    // Peaks inside a clip the streamer already marked need no suggesting -- the
    // moment is in the recording either way.
    const covered = (offsetMs) => reactions.some((r) => offsetMs >= r.start_ms && offsetMs <= r.end_ms);
    const shouts = voice
        .filter((v) => !covered(v.offsetMs))
        // Sorted before the cap, so what survives it is the strongest and not
        // merely the earliest.
        .sort((a, b) => b.multiple - a.multiple)
        .slice(0, MAX_VOICE_SUGGESTIONS);
    for (const v of shouts) {
        const start_ms = Math.max(0, v.offsetMs - VOICE_LEAD_MS);
        const end_ms = v.offsetMs + VOICE_TAIL_MS;
        const existing = out.find((w) => start_ms < w.end_ms && end_ms > w.start_ms);
        if (existing) {
            // Overlapping an audience suggestion promotes it rather than adding a
            // row. Overlapping another shout is simply dropped: the window is nine
            // seconds and the detector's cooldown is thirty, so this only happens
            // through malformed input.
            if (existing.source === "audience")
                existing.source = "both";
            continue;
        }
        out.push({ start_ms, end_ms, hits: 0, kind: "", source: "voice" });
    }
    // Stable, so each tier keeps the order it was built in: `both` first,
    // audience by hits, then the shouts by how loud they were.
    const tier = (s) => (s === "both" ? 0 : s === "audience" ? 1 : 2);
    return out.sort((a, b) => tier(a.source) - tier(b.source));
}
/**
 * How far from a session's end a recording may still belong to it. Generous,
 * because remuxing or a slow flush can delay the final write, but far short of
 * the gap between two streams.
 */
export const SESSION_MATCH_TOLERANCE_MS = 30 * 60 * 1000;
/**
 * The session a recording most likely belongs to, or null when nothing fits.
 *
 * Returning null is a real answer: guessing at a session an hour away would
 * attach someone's reactions to the wrong stream, which is worse than leaving
 * it unlinked and saying so.
 */
export function matchSession(recordedAt, sessions) {
    const at = new Date(recordedAt).getTime();
    if (!Number.isFinite(at))
        return null;
    let best = null;
    for (const s of sessions) {
        if (s.has_recording)
            continue;
        const start = new Date(s.started_at).getTime();
        const end = s.ended_at ? new Date(s.ended_at).getTime() : start;
        if (!Number.isFinite(start) || !Number.isFinite(end))
            continue;
        // Inside the session's own span is a certain match; otherwise measure from
        // whichever edge is nearer.
        const distance = at >= start && at <= end ? 0 : at < start ? start - at : at - end;
        if (distance > SESSION_MATCH_TOLERANCE_MS)
            continue;
        if (!best || distance < best.distance)
            best = { id: s.id, distance };
    }
    return best?.id ?? null;
}
// --- Minimum supported client ----------------------------------------------
/**
 * Whether a Studio build is too old to start a session.
 *
 * Updates are deliberately optional — a streamer is live in front of an
 * audience and an app that forces a restart is worse than one that is out of
 * date — so old clients exist in the wild indefinitely. That is fine until a
 * server change breaks one, at which point the streamer sees a cryptic failure
 * and has no idea the fix is to update.
 *
 * This turns that into a sentence they can act on. It is a usability guard, not
 * a security one: anything unparseable is allowed through, because being locked
 * out of a product you have paid for is a far worse failure than running an old
 * build.
 */
/** "1.2.3" -> [1, 2, 3]. Null when it is not a version at all. */
export function parseVersion(v) {
    // A trailing pre-release or build suffix is ignored rather than rejected:
    // "0.2.0-beta.1" is still usefully comparable as 0.2.0.
    const m = /^\s*v?(\d+)\.(\d+)\.(\d+)/.exec(v);
    if (!m)
        return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
}
/** -1, 0 or 1. Null when either side is unparseable. */
export function compareVersions(a, b) {
    const pa = parseVersion(a);
    const pb = parseVersion(b);
    if (!pa || !pb)
        return null;
    for (let i = 0; i < 3; i++) {
        const d = (pa[i] ?? 0) - (pb[i] ?? 0);
        if (d !== 0)
            return d < 0 ? -1 : 1;
    }
    return 0;
}
/**
 * True only when we are certain the client is older than the minimum.
 *
 * Every uncertain case — no minimum configured, no version reported, a version
 * neither side can parse — returns false. Refusing on a guess would strand a
 * paying streamer minutes before they go live.
 */
export function isBelowMinimum(clientVersion, minimum) {
    if (!minimum || !minimum.trim())
        return false;
    if (!clientVersion || !clientVersion.trim())
        return false;
    const cmp = compareVersions(clientVersion, minimum);
    return cmp === null ? false : cmp < 0;
}
/**
 * The games played during a session, from its `game_start` / `game_end` notes.
 *
 * Tolerant in three ways that all come from the same fact: `game_end` is
 * best-effort. A game is recognised as closed by its executable leaving the
 * process list, so a session can legitimately end with a start and no end, and
 * Studio can crash between the two.
 *
 * - A start with no end stays open, ending at `session_end` if there is one.
 * - A second start closes the first span, because two games cannot be the
 *   answer for one instant.
 * - An end with no start is ignored rather than inventing a span.
 */
export function gameSpans(events) {
    const spans = [];
    let open = null;
    let sessionEnd = null;
    const meta = (e) => (e.metadata ?? {});
    const str = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
    for (const e of events) {
        if (e.kind === "session_end") {
            sessionEnd = e.offset_ms;
            continue;
        }
        if (e.kind === "game_start") {
            const m = meta(e);
            const id = str(m.id);
            const name = str(m.name);
            // Without an identity there is nothing to attribute a clip to, and a span
            // labelled "unknown" is worse than no span: it would group every
            // unidentifiable game together as though they were one.
            if (!id && !name)
                continue;
            if (open) {
                open.end_ms = e.offset_ms;
                spans.push(open);
            }
            open = {
                id: id ?? name,
                name: name ?? id,
                store: str(m.store) ?? "",
                start_ms: e.offset_ms,
                end_ms: null,
            };
            continue;
        }
        if (e.kind === "game_end" && open) {
            open.end_ms = e.offset_ms;
            spans.push(open);
            open = null;
        }
    }
    if (open) {
        open.end_ms = sessionEnd;
        spans.push(open);
    }
    return spans;
}
/**
 * The game running at a moment, or null.
 *
 * Callers pass a clip's **midpoint**, not its start. Clip boundaries carry
 * padding — a second and a half before the reaction began — so the first clip
 * of a game can legitimately start before the `game_start` that caused it. The
 * midpoint is inside the span for any clip that actually happened during the
 * game, which is the question being asked.
 */
export function gameAt(spans, offsetMs) {
    for (const s of spans) {
        const end = s.end_ms ?? Number.POSITIVE_INFINITY;
        if (offsetMs >= s.start_ms && offsetMs < end)
            return s;
    }
    return null;
}
/** The game a reaction belongs to, by its midpoint. */
export function gameForReaction(spans, reaction) {
    return gameAt(spans, Math.round((reaction.start_ms + reaction.end_ms) / 2));
}
//# sourceMappingURL=index.js.map